import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button, Grid, Header, Icon, Image,
    Label, Message, Segment, Tab, TabPane
} from 'semantic-ui-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import LoadingComponent from '../../app/layout/LoadingComponent';
import ChallengesTab from './ChallengesTab';
import TeamSearchPage from './TeamSearchPage';

// ── Tab index constants ────────────────────────────────────────────────────────
const TAB_MY_TEAMS   = 0;
const TAB_CHALLENGES = 1;
const TAB_FIND_TEAMS = 2;

export default observer(function TeamDashboard() {
    const { teamStore, teamMatchStore } = useStore();
    const { t } = useTranslation();
    const location = useLocation();

    const {
        myTeams, myInvitations, loading, submitting,
        loadMyTeams, loadMyInvitations, respondToInvitation,
    } = teamStore;

    const { pendingChallenges, loadPendingChallenges } = teamMatchStore;

    // Deep-link support: ?tab=challenges&teamId=<id>  or  ?tab=find
    const getInitialTab = () => {
        const p = new URLSearchParams(location.search);
        if (p.get('tab') === 'challenges') return TAB_CHALLENGES;
        if (p.get('tab') === 'find')       return TAB_FIND_TEAMS;
        return TAB_MY_TEAMS;
    };

    const [activeTab,          setActiveTab]          = useState(getInitialTab);
    // FIX #1 — team ID forwarded when user clicks Challenge on a team card
    const [preselectedTeamId,  setPreselectedTeamId]  = useState(
        () => new URLSearchParams(location.search).get('teamId') ?? ''
    );

    useEffect(() => {
        loadMyTeams();
        loadMyInvitations();
        loadPendingChallenges();
    }, []);


    // ── Pending action status per team ─────────────────────────────────────────
    const getTeamActionStatus = (teamId: string) => {
        const relevant        = pendingChallenges.filter(c => c.challengerTeamId === teamId || c.challengedTeamId === teamId);
        const receivedCount   = relevant.filter(c => c.type === 'ReceivedChallenge').length;
        const appliedCount    = relevant.filter(c => c.type === 'TeamApplied').length;
        const sentCount       = relevant.filter(c => c.type === 'SentChallenge').length;
        const activeDuelCount = relevant.filter(c => c.type === 'ActiveDuel').length;
        const scheduledCount  = relevant.filter(c => c.type === 'ScheduledMatch').length;
        return {
            needsAction: receivedCount > 0 || appliedCount > 0,
            hasPending: relevant.length > 0,
            receivedCount, appliedCount, sentCount, activeDuelCount, scheduledCount,
        };
    };

    const totalActionCount = pendingChallenges.filter(
        c => c.type === 'ReceivedChallenge' || c.type === 'TeamApplied'
    ).length;

    const handleChallengeClick = (teamId: string) => {
        setPreselectedTeamId(teamId);
        setActiveTab(TAB_CHALLENGES);
    };

    const handleTabChange = (_: unknown, data: { activeIndex?: string | number }) => {
        const idx = data.activeIndex as number;
        setActiveTab(idx);
        // Clear preselection only if navigating away from Challenges
        if (idx !== TAB_CHALLENGES) setPreselectedTeamId('');
    };

    // ── Panes ──────────────────────────────────────────────────────────────────
    const panes = [
        {
            menuItem: {
                key: 'myteams',
                content: (
                    <span>
                        <Icon name="shield" />
                        {t('teamDashboard.tabs.myTeams')}
                    </span>
                ),
            },
            render: () => (
                <TabPane>
                    <MyTeamsTab
                        myTeams={myTeams}
                        myInvitations={myInvitations}
                        submitting={submitting}
                        respondToInvitation={respondToInvitation}
                        getTeamActionStatus={getTeamActionStatus}
                        t={t}
                        onChallengeClick={handleChallengeClick}
                    />
                </TabPane>
            ),
        },
        {
            menuItem: {
                key: 'challenges',
                content: (
                    <span>
                        <Icon name="shield alternate" />
                        {t('teamDashboard.tabs.challenges')}
                        {totalActionCount > 0 && (
                            <Label circular color="orange" size="mini"
                                style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                                {totalActionCount}
                            </Label>
                        )}
                    </span>
                ),
            },
            render: () => (
                <TabPane>
                    <ChallengesTab preselectedTeamId={preselectedTeamId} />
                </TabPane>
            ),
        },
        {
            menuItem: {
                key: 'findteams',
                content: (
                    <span>
                        <Icon name="search" />
                        {t('teamDashboard.tabs.findTeams')}
                    </span>
                ),
            },
            render: () => (
                <TabPane>
                    <div style={{ margin: '-14px -14px 0' }}>
                        <TeamSearchPage embedded />
                    </div>
                </TabPane>
            ),
        },
    ];

    return (
        <Grid>
            <Grid.Column width={16}>
                <Tab
                    panes={panes}
                    activeIndex={activeTab}
                    onTabChange={handleTabChange}
                    menu={{ secondary: true, pointing: true }}
                />
            </Grid.Column>
        </Grid>
    );
});

// ── My Teams inner tab ─────────────────────────────────────────────────────────
interface MyTeamsTabProps {
    myTeams: import('../../app/models/Team').TeamDto[];
    myInvitations: import('../../app/models/Team').TeamInvitationDto[];
    submitting: boolean;
    respondToInvitation: (id: string, accept: boolean) => Promise<boolean>;
    getTeamActionStatus: (teamId: string) => {
        needsAction: boolean; hasPending: boolean;
        receivedCount: number; appliedCount: number; sentCount: number;
        activeDuelCount: number; scheduledCount: number;
    };
    t: (key: string, opts?: Record<string, unknown>) => string;
    onChallengeClick: (teamId: string) => void;
}

const MyTeamsTab = observer(function MyTeamsTab({
    myTeams, myInvitations, submitting,
    respondToInvitation, getTeamActionStatus, t, onChallengeClick,
}: MyTeamsTabProps) {
    return (
        <div>
            {/* Pending invitations */}
            {myInvitations.length > 0 && (
                <Segment style={{ marginBottom: 16 }}>
                    <Header as="h4" style={{ marginBottom: 12 }}>
                        <Icon name="mail" />
                        <Header.Content>
                            {t('teams.invitations.pendingTitle')}
                            <Label circular color="blue" size="small"
                                style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                                {myInvitations.length}
                            </Label>
                        </Header.Content>
                    </Header>
                    {myInvitations.map(inv => (
                        <Message key={inv.id} info style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Image src={inv.teamLogoUrl || '/assets/team-placeholder.png'} avatar style={{ width: 40, height: 40 }} />
                                    <div>
                                        <strong style={{ fontSize: '1.05em' }}>{inv.teamName}</strong>
                                        <div style={{ fontSize: '0.85em', color: '#555', marginTop: 2 }}>
                                            <Image src={inv.invitedByUserImageUrl || '/assets/user.png'} avatar style={{ width: 18, height: 18, marginRight: 4 }} />
                                            {t('teams.invitations.invitedBy')}: <strong>{inv.invitedByUserFullName}</strong>
                                        </div>
                                        <div style={{ fontSize: '0.78em', color: '#999', marginTop: 2 }}>
                                            {new Date(inv.invitedAt).toLocaleDateString()}{' '}
                                            {new Date(inv.invitedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                    <Button positive size="small" loading={submitting} disabled={submitting}
                                        onClick={() => respondToInvitation(inv.id, true)}>
                                        <Icon name="check" />{t('teams.invitations.accept')}
                                    </Button>
                                    <Button negative size="small" loading={submitting} disabled={submitting}
                                        onClick={() => respondToInvitation(inv.id, false)}>
                                        <Icon name="close" />{t('teams.invitations.decline')}
                                    </Button>
                                </div>
                            </div>
                        </Message>
                    ))}
                </Segment>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button primary as={Link} to="/createTeam" size="small">
                    <Icon name="plus" />{t('teams.createTeam')}
                </Button>
            </div>

            {myTeams.length === 0 ? (
                <Message info>
                    <Icon name="info circle" />
                    {t('teams.noTeamsYet')}
                    <Button as={Link} to="/createTeam" size="mini" primary style={{ marginLeft: 12 }}>
                        {t('teams.createTeam')}
                    </Button>
                </Message>
            ) : (
                <div>
                    {myTeams.map(team => (
                        <TeamCard
                            key={team.id}
                            team={team}
                            actionStatus={getTeamActionStatus(team.id)}
                            t={t}
                            onChallengeClick={() => onChallengeClick(team.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
});

// ── Team card ──────────────────────────────────────────────────────────────────
interface TeamCardProps {
    team: import('../../app/models/Team').TeamDto;
    actionStatus: ReturnType<MyTeamsTabProps['getTeamActionStatus']>;
    t: (key: string, opts?: Record<string, unknown>) => string;
    onChallengeClick: () => void;
}

const TeamCard = observer(function TeamCard({ team, actionStatus, t, onChallengeClick }: TeamCardProps) {
    const { teamStore } = useStore();
    const navigate = useNavigate();
    const [confirmLeave,    setConfirmLeave]    = useState(false);
    const [localSubmitting, setLocalSubmitting] = useState(false);

    const handleStatusToggle = async (newStatus: 'Active' | 'Inactive') => {
        setLocalSubmitting(true);
        await teamStore.updateTeamStatus(team.id, newStatus);
        setLocalSubmitting(false);
    };

    const handleLeave = async () => {
        setLocalSubmitting(true);
        const ok = await teamStore.leaveTeam(team.id);
        if (ok) setConfirmLeave(false);
        else    setLocalSubmitting(false);
    };

    const statusColor =
        team.status === 'Active'   ? 'green' :
        team.status === 'Inactive' ? 'grey'  : 'red';

    return (
        <Segment
            style={{
                marginBottom: 12,
                borderLeft: actionStatus.needsAction ? '4px solid #f97316' : undefined,
                padding: '14px 16px',
            }}
        >
            {/* ── Clickable profile header ───────────────────────────────── */}
            <div
                role="button"
                tabIndex={0}
                aria-label={t('teamDashboard.openTeamProfile', { name: team.name })}
                onClick={() => navigate(`/teams/${team.id}`)}
                onKeyDown={e => e.key === 'Enter' && navigate(`/teams/${team.id}`)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    cursor: 'pointer', marginBottom: 12,
                    padding: '6px 8px', borderRadius: 8, transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
                <Image
                    src={team.logoUrl || '/assets/team-placeholder.png'}
                    avatar
                    style={{ width: 44, height: 44, objectFit: 'cover' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Name + badge row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '1em', color: '#1a5f8a' }}>
                            {team.name}
                        </span>
                        {actionStatus.needsAction && (
                            <Icon name="bell" color="orange" style={{ margin: 0 }} />
                        )}
                        <Label size="tiny" color={statusColor as any}>
                            {t(`teams.status.${team.status.toLowerCase()}`)}
                        </Label>
                        {team.isCurrentUserCaptain && (
                            <Label size="tiny" color="blue">
                                <Icon name="star" />{t('teams.roles.captain')}
                            </Label>
                        )}
                    </div>

                    {/* Sport + members */}
                    <div style={{ fontSize: '0.82em', color: '#888', marginTop: 3 }}>
                        {team.sportName}
                        <span style={{ marginLeft: 10 }}>
                            <Icon name="user" size="small" />
                            {team.teamMembers.length} {t('teams.members')}
                        </span>
                    </div>

                    {(actionStatus.receivedCount > 0 || actionStatus.appliedCount > 0 ||
                      actionStatus.sentCount > 0 || actionStatus.activeDuelCount > 0 ||
                      actionStatus.scheduledCount > 0) && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                            {actionStatus.receivedCount > 0 && (
                                <Label size="mini" color="yellow">
                                    <Icon name="bell" />
                                    {actionStatus.receivedCount} {t('teamDashboard.labels.receivedChallenge', { count: actionStatus.receivedCount })}
                                </Label>
                            )}
                            {actionStatus.appliedCount > 0 && (
                                <Label size="mini" color="orange">
                                    <Icon name="users" />
                                    {actionStatus.appliedCount} {t('teamDashboard.labels.teamApplied', { count: actionStatus.appliedCount })}
                                </Label>
                            )}
                            {actionStatus.sentCount > 0 && (
                                <Label size="mini" color="blue">
                                    <Icon name="send" />
                                    {actionStatus.sentCount} {t('teamDashboard.labels.sentChallenge', { count: actionStatus.sentCount })}
                                </Label>
                            )}
                            {actionStatus.activeDuelCount > 0 && (
                                <Label size="mini" color="purple">
                                    <Icon name="handshake" />
                                    {actionStatus.activeDuelCount} {t('teamDashboard.labels.activeDuel', { count: actionStatus.activeDuelCount })}
                                </Label>
                            )}
                            {actionStatus.scheduledCount > 0 && (
                                <Label size="mini" color="teal">
                                    <Icon name="calendar check" />
                                    {actionStatus.scheduledCount} {t('teamDashboard.labels.scheduledMatch', { count: actionStatus.scheduledCount })}
                                </Label>
                            )}
                        </div>
                    )}
                </div>

                <span style={{ fontSize: '0.75em', color: '#bbb', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {t('teamDashboard.openProfile')} ↗
                </span>
            </div>

            {/* ── Quick-action buttons ───────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>

                {team.isCurrentUserCaptain && team.status === 'Active' && (
                    <Button size="mini" color="orange" onClick={onChallengeClick}>
                        <Icon name="shield alternate" />
                        {t('teamDashboard.actions.challenge')}
                    </Button>
                )}

                {team.isCurrentUserCaptain && team.status === 'Active' && (
                    <Button size="mini" primary
                        onClick={() => navigate(`/teams/${team.id}`, { state: { openInvite: true } })}>
                        <Icon name="user plus" />
                        {t('teamDashboard.actions.invitePlayer')}
                    </Button>
                )}

                {team.isCurrentUserCaptain && (
                    <Button size="mini" basic as={Link} to={`/teams/${team.id}/edit`}>
                        <Icon name="edit" />
                        {t('teamDashboard.actions.editTeam')}
                    </Button>
                )}

                {team.isCurrentUserCaptain && actionStatus.receivedCount > 0 && (
                    <Button size="mini" color="yellow" as={Link} to={`/teams/${team.id}`}>
                        <Icon name="bell" />
                        {t('teamDashboard.actions.viewRequests')}
                        <Label circular size="mini"
                            style={{ marginLeft: 5, background: 'rgba(0,0,0,0.15)', color: '#fff' }}>
                            {actionStatus.receivedCount}
                        </Label>
                    </Button>
                )}

                {team.isCurrentUserMember && !team.isCurrentUserCaptain && (
                    <Button size="mini" basic color="red"
                        loading={localSubmitting} disabled={localSubmitting}
                        onClick={() => setConfirmLeave(true)}>
                        <Icon name="sign-out" />
                        {t('teams.leaveTeam')}
                    </Button>
                )}

                {team.isCurrentUserCaptain && team.status === 'Active' && (
                    <Button size="mini" basic color="grey"
                        loading={localSubmitting} disabled={localSubmitting}
                        onClick={() => handleStatusToggle('Inactive')}>
                        <Icon name="pause" />
                        {t('teams.deactivate')}
                    </Button>
                )}
                {team.isCurrentUserCaptain && team.status === 'Inactive' && (
                    <Button size="mini" basic color="green"
                        loading={localSubmitting} disabled={localSubmitting}
                        onClick={() => handleStatusToggle('Active')}>
                        <Icon name="play" />
                        {t('teams.activate')}
                    </Button>
                )}
            </div>

            {/* Leave confirm inline */}
            {confirmLeave && (
                <Message warning style={{ marginTop: 10 }}>
                    <p>{t('teams.confirmLeave')}</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button negative size="mini" loading={localSubmitting} onClick={handleLeave}>
                            {t('teams.leaveTeam')}
                        </Button>
                        <Button basic size="mini" onClick={() => setConfirmLeave(false)}>
                            {t('teams.cancel')}
                        </Button>
                    </div>
                </Message>
            )}
        </Segment>
    );
});